import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calculator } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './AdditiveManufacturingCostCalculatorPage/components/CalculationForm';
import ResultsOverview from './AdditiveManufacturingCostCalculatorPage/components/ResultsOverview';
import MaterialBreakdown from './AdditiveManufacturingCostCalculatorPage/components/MaterialBreakdown';
import QuickAdjustmentSidebar from './AdditiveManufacturingCostCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './AdditiveManufacturingCostCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './AdditiveManufacturingCostCalculatorPage/components/TipsSidebar';
import InfoContent from './AdditiveManufacturingCostCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  // Параметры модели
  modelVolume: '100', // Объем в см³
  modelWeight: '250', // Вес в граммах
  modelHeight: '10', // Высота в см
  printQuantity: '1', // Количество изделий
  
  // Параметры материала
  materialType: 'pla', // Тип материала (PLA, ABS, PETG и т.д.)
  materialDensity: '1.24', // Плотность материала г/см³
  materialCostPerKg: '1500', // Стоимость за кг материала
  supportsRequired: true, // Требуются ли поддержки
  supportDensity: '30', // Плотность поддержек в процентах
  
  // Параметры принтера и процесса
  printerType: 'fdm', // Тип принтера (FDM, SLA, SLS)
  printerCost: '25000', // Стоимость принтера
  electricityCost: '5.5', // Стоимость электроэнергии за кВт⋅ч
  printerConsumption: '0.25', // Потребление электроэнергии в кВт
  printSpeed: '50', // Скорость печати в мм/с
  layerHeight: '0.2', // Высота слоя в мм
  nozzleDiameter: '0.4', // Диаметр сопла в мм
  infillDensity: '20', // Плотность заполнения в процентах
  
  // Трудовые затраты
  laborCostPerHour: '300', // Стоимость рабочего времени за час
  preparationTime: '0.5', // Время на подготовку модели (часы)
  postProcessingTime: '0.5', // Время на постобработку (часы)
  
  // Амортизация и другие расходы
  includeDepreciation: true, // Учитывать амортизацию принтера
  depreciationPeriod: '36', // Срок амортизации в месяцах
  printerMonthlyUse: '80', // Часов использования принтера в месяц
  includeFailureRate: true, // Учитывать процент брака
  failureRate: '5', // Процент брака
  
  // Прочие расходы и наценка
  includeOverheads: true, // Учитывать накладные расходы
  overheadPercentage: '15', // Процент накладных расходов
  includeProfit: true, // Включить прибыль
  profitMargin: '30', // Маржа прибыли в процентах
  
  // Дополнительные параметры
  currency: 'RUB', // Валюта расчета
};

// Определение типов материалов
export const materialTypes = [
  { id: 'pla', name: 'PLA', density: 1.24, costPerKg: 1500, preferredPrinterType: 'fdm' },
  { id: 'abs', name: 'ABS', density: 1.04, costPerKg: 1650, preferredPrinterType: 'fdm' },
  { id: 'petg', name: 'PETG', density: 1.27, costPerKg: 1800, preferredPrinterType: 'fdm' },
  { id: 'nylon', name: 'Нейлон', density: 1.15, costPerKg: 3500, preferredPrinterType: 'fdm' },
  { id: 'tpu', name: 'TPU (Гибкий)', density: 1.21, costPerKg: 3000, preferredPrinterType: 'fdm' },
  { id: 'resin', name: 'Фотополимерная смола', density: 1.10, costPerKg: 7000, preferredPrinterType: 'sla' },
  { id: 'nylon_powder', name: 'Нейлоновый порошок', density: 0.95, costPerKg: 5000, preferredPrinterType: 'sls' },
  { id: 'metal_powder', name: 'Металлический порошок', density: 4.41, costPerKg: 15000, preferredPrinterType: 'slm' }
];

// Определение типов принтеров
export const printerTypes = [
  { id: 'fdm', name: 'FDM (Послойное наплавление)', avgCost: 25000, powerConsumption: 0.25 },
  { id: 'sla', name: 'SLA (Стереолитография)', avgCost: 90000, powerConsumption: 0.12 },
  { id: 'sls', name: 'SLS (Селективное лазерное спекание)', avgCost: 350000, powerConsumption: 1.5 },
  { id: 'slm', name: 'SLM (Селективное лазерное плавление)', avgCost: 1200000, powerConsumption: 3.5 },
  { id: 'polyjet', name: 'PolyJet (Струйная печать)', avgCost: 500000, powerConsumption: 1.0 }
];

/**
 * AdditiveManufacturingCostCalculatorPage Component - Полный калькулятор стоимости 3D-печати и аддитивного производства
 */
const AdditiveManufacturingCostCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('additive-manufacturing-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор стоимости 3D-печати | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный онлайн-калькулятор стоимости 3D-печати: расчет себестоимости и рыночной стоимости изделий с учетом материалов, времени печати, амортизации оборудования и трудозатрат.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [materialBreakdown, setMaterialBreakdown] = useState([]);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcData = localStorage.getItem('savedAdditiveCalculations');
    if (savedCalcData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'fdm':
          newState.printerType = 'fdm';
          newState.materialType = 'pla';
          break;
        case 'sla':
          newState.printerType = 'sla';
          newState.materialType = 'resin';
          break;
        case 'sls':
          newState.printerType = 'sls';
          newState.materialType = 'nylon_powder';
          break;
        case 'metal':
          newState.printerType = 'slm';
          newState.materialType = 'metal_powder';
          break;
        case 'production':
          newState.printQuantity = '100';
          newState.includeProfit = true;
          newState.profitMargin = '40';
          break;
        case 'prototyping':
          newState.printQuantity = '1';
          newState.includeProfit = false;
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setMaterialBreakdown([]);
    setErrors({});

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalculation = {
      id: Date.now().toString(),
      name: name || `${formData.materialType.toUpperCase()} ${formData.modelVolume}cm³ x${formData.printQuantity}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      printerType: formData.printerType
    };

    const updatedSavedCalculations = [...savedCalculations, newSavedCalculation];
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedAdditiveCalculations', JSON.stringify(updatedSavedCalculations));

    return newSavedCalculation;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalculations = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalculations);
    localStorage.setItem('savedAdditiveCalculations', JSON.stringify(updatedSavedCalculations));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // Пересчитаем детализацию материалов для визуализации
      // В реальном кейсе нужно будет либо хранить детализацию в сохраненном расчете,
      // либо заново вызвать расчетную функцию
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calculator className="w-8 h-8 text-teal-600 dark:text-teal-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор стоимости 3D-печати'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              materialBreakdown={materialBreakdown}
              setMaterialBreakdown={setMaterialBreakdown}
              materialTypes={materialTypes}
              printerTypes={printerTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} />
                <MaterialBreakdown 
                  materialBreakdown={materialBreakdown} 
                  results={results}
                />
              </>
            )}

            {/* --- Информационный контент о 3D-печати --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              materialTypes={materialTypes}
              printerTypes={printerTypes}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-teal-600 focus:ring-teal-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for tables */
        .custom-scrollbar {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .custom-scrollbar {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .custom-scrollbar::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default AdditiveManufacturingCostCalculatorPage;